import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { putCompanyRolePermissions } from '../../../api/company/company';
import './RolePermissionsMatrix.css';

const ROLES = ['owner', 'manager', 'employee'];

const formatGroupName = (groupKey) =>
  groupKey
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

const formatPermissionName = (permKey) => {
  const name = permKey.toLowerCase();
  switch (name) {
    case 'view':
      return 'View Page';
    case 'view_all':
      return 'View All';
    case 'view_privacy':
      return 'Privacy Settings';
    case 'edit':
      return 'Edit';
    case 'create':
      return 'Create';
    case 'delete':
      return 'Delete';
    case 'manage':
      return 'Manage';
    case 'archive':
      return 'Archive';
    case 'edit_account':
      return 'Edit Account';
    case 'export':
      return 'Export';
    default:
      return name.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
};

const getPermissionHint = (groupName, permKey, permValue) =>
  `${formatGroupName(groupName)} — ${formatPermissionName(permKey)} (${permValue}). Click the checkbox to grant or remove this permission for the role.`;

const RolePermissionsMatrix = ({ permissions, rolePermissions: initialRolePermissions }) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [rolePermissions, setRolePermissions] = useState(initialRolePermissions || {});
  const justAppliedLocalChangeRef = useRef(false);
  const isMountedRef = useRef(true);
  const { companyData, user, updateUser, updateCompanyData } = useAuth();
  const { setNotification } = useNotification();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialRolePermissions) {
      if (!justAppliedLocalChangeRef.current) {
        setRolePermissions(initialRolePermissions);
      }
      justAppliedLocalChangeRef.current = false;
    }
  }, [initialRolePermissions]);

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const isPermissionGranted = (role, permission) => {
    if (!rolePermissions?.[role]) return false;
    return rolePermissions[role].includes(permission);
  };

  const isCheckboxDisabled = (role) =>
    role === 'owner' ||
    user?.role === 'employee' ||
    (user?.role === 'manager' && (role === 'owner' || role === 'manager'));

  const getCheckboxTitle = (role, permKey, permValue, disabled) => {
    if (disabled) {
      if (role === 'owner') return 'Owner permissions are locked for non-owners';
      if (user?.role === 'employee') return 'Only owners and managers can change permissions';
      if (user?.role === 'manager') return 'Managers can only edit employee permissions';
      return 'This permission cannot be changed';
    }
    const action = isPermissionGranted(role, permValue) ? 'Remove' : 'Grant';
    return `${action} "${formatPermissionName(permKey)}" for ${role}s`;
  };

  const handlePermissionChange = async (role, permission) => {
    if (user?.role !== 'owner' && user?.role !== 'manager') {
      setNotification({
        open: true,
        message: 'Only owners and managers can modify permissions',
        severity: 'error',
      });
      return;
    }

    if (role === 'owner' && user?.role !== 'owner') {
      setNotification({
        open: true,
        message: 'Only owners can modify owner permissions',
        severity: 'error',
      });
      return;
    }

    if (user?.role === 'manager' && (role === 'owner' || role === 'manager')) {
      setNotification({
        open: true,
        message: 'Managers can only modify employee permissions',
        severity: 'error',
      });
      return;
    }

    if (!companyData?.id || !companyData?.ownerId) {
      setNotification({
        open: true,
        message: 'Company data is missing. Please refresh and try again.',
        severity: 'error',
      });
      return;
    }

    const previousRolePermissions = { ...rolePermissions };
    const newRolePermissions = { ...rolePermissions };
    const rolePerms = new Set(newRolePermissions[role] || []);

    if (rolePerms.has(permission)) {
      rolePerms.delete(permission);
    } else {
      rolePerms.add(permission);
    }
    newRolePermissions[role] = Array.from(rolePerms);

    justAppliedLocalChangeRef.current = true;
    setRolePermissions(newRolePermissions);
    if (user?.role === role) {
      try {
        updateUser({
          ...user,
          permissions: newRolePermissions[role] || [],
        });
      } catch (_) {
        /* ignore */
      }
    }
    setNotification({
      open: true,
      message: `Permissions updated for all ${role}s. Saving in the background—you can switch pages; it may take a few seconds to a minute for changes to fully apply.`,
      severity: 'success',
    });

    const companyId = companyData.id;
    putCompanyRolePermissions({
      companyId,
      role,
      permissions: newRolePermissions[role],
    })
      .then((result) => {
        if (!isMountedRef.current) return;
        if (result?.company && updateCompanyData) {
          updateCompanyData({ ...companyData, ...result.company });
        }
      })
      .catch((error) => {
        console.error('Error updating permissions:', error);
        if (!isMountedRef.current) return;
        setRolePermissions(previousRolePermissions);
        if (user?.role === role) {
          try {
            updateUser({ ...user, permissions: previousRolePermissions[role] || [] });
          } catch (_) {
            /* ignore */
          }
        }
        setNotification({
          open: true,
          message: error.message || 'Failed to save. Please try again.',
          severity: 'error',
        });
      });
  };

  return (
    <div className="rpm-root">
      <div className="rpm-card">
        <div className="rpm-scroll">
          <table className="rpm-table" aria-label="Role permissions">
            <colgroup>
              <col className="rpm-col-perm" />
              {ROLES.map((role) => (
                <col key={role} className="rpm-col-role" />
              ))}
            </colgroup>
            <thead className="rpm-thead">
              <tr>
                <th scope="col">Permission</th>
                {ROLES.map((role) => (
                  <th key={role} scope="col">
                    <span className="rpm-role-head">
                      <span className="rpm-role-head__label">{role}</span>
                      {role === 'owner' && user?.role !== 'owner' ? (
                        <span className="rpm-role-head__lock" aria-hidden="true">
                          Locked
                        </span>
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(permissions).map(([groupName, groupPermissions]) => {
                const isExpanded = expandedGroups[groupName] ?? true;
                const permEntries = Object.entries(groupPermissions);

                return (
                  <React.Fragment key={groupName}>
                    <tr className="rpm-group-row">
                      <td colSpan={ROLES.length + 1}>
                        <button
                          type="button"
                          className="rpm-group-btn"
                          onClick={() => toggleGroup(groupName)}
                          aria-expanded={isExpanded}
                        >
                          <svg
                            className={`rpm-chevron ${isExpanded ? '' : 'is-collapsed'}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {formatGroupName(groupName)}
                          <span className="rpm-group-count">
                            {permEntries.length} permission{permEntries.length === 1 ? '' : 's'}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {isExpanded &&
                      permEntries.map(([permKey, permValue]) => (
                        <tr key={permValue} className="rpm-perm-row">
                          <td>
                            <div className="rpm-perm-name">
                              <span className="rpm-perm-label">{formatPermissionName(permKey)}</span>
                              <button
                                type="button"
                                className="rpm-help"
                                title={getPermissionHint(groupName, permKey, permValue)}
                                aria-label={getPermissionHint(groupName, permKey, permValue)}
                              >
                                ?
                              </button>
                            </div>
                          </td>
                          {ROLES.map((role) => {
                            const disabled = isCheckboxDisabled(role);
                            const checked = isPermissionGranted(role, permValue);
                            const inputId = `perm-${groupName}-${permKey}-${role}`;

                            return (
                              <td key={`${role}-${permValue}`} className="rpm-cell-role">
                                <div className="rpm-check-wrap">
                                  <input
                                    type="checkbox"
                                    id={inputId}
                                    className="rpm-check"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => handlePermissionChange(role, permValue)}
                                    title={getCheckboxTitle(role, permKey, permValue, disabled)}
                                    aria-label={`${formatPermissionName(permKey)} for ${role}`}
                                  />
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="rpm-legend" aria-hidden="false">
          <span className="rpm-legend-item">
            <span className="rpm-legend-sample rpm-legend-sample--on" />
            Permission granted
          </span>
          <span className="rpm-legend-item">
            <span className="rpm-legend-sample rpm-legend-sample--off" />
            Not granted
          </span>
          <span className="rpm-legend-item">
            <span className="rpm-legend-sample rpm-legend-sample--locked" />
            Locked / view only
          </span>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionsMatrix;
